import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import BookCard from "./BookCard";
import SkeletonCard from "../uiComponents/SkeletonCard"; // <- make sure this is imported
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const CustomArrow = ({ onClick, direction, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={`${direction} slide`}
    className={`absolute ${
      direction === "left" ? "-left-16" : "-right-16"
    } top-1/2 -translate-y-1/2 z-10 bg-white/90 shadow-md p-2 rounded-full hover:bg-primary-50 transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d={direction === "left" ? "M15 18L9 12L15 6" : "M9 18L15 12L9 6"}
        stroke="#FF7236"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const HomeBookSection = ({ title, books = [], onSeeAll, loading = false }) => {
  const navigate = useNavigate();
  const wheelGesturesPlugin = useMemo(() => WheelGesturesPlugin(), []);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: true,
      breakpoints: {
        "(min-width:    0px)": { slidesToScroll: 2 },
        "(min-width:  640px)": { slidesToScroll: 3 },
        "(min-width:  768px)": { slidesToScroll: 4 },
        "(min-width: 1024px)": { slidesToScroll: 6 },
        "(min-width: 1280px)": { slidesToScroll: 8 },
      },
    },
    [wheelGesturesPlugin]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi?.scrollTo(index), [emblaApi]);

  const updateState = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", updateState);
    updateState();
    return () => emblaApi.off("select", updateState);
  }, [emblaApi, updateState]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") scrollPrev();
      if (e.key === "ArrowRight") scrollNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollPrev, scrollNext]);

  const handleSeeAll = () => {
    navigate("/justlaunched-books");
  };

  const placeholderCount = 10; // adjust based on design

  return (
    <section className="relative mb-6 bg-white py-1">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-2 px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-text-muted border-b-2 border-primary pb-1">
            {title}
          </h2>
          <button
            onClick={onSeeAll}
            className="flex items-center gap-2 text-primary hover:text-primary-shade4 transition-colors group"
            aria-label="See all books"
          >
            <span className="font-medium">See All</span>
            <div className="flex -space-x-2 overflow-hidden">
              {[0, 0.15].map((delay, i) => (
                <motion.svg
                  key={i}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-5 h-5"
                  animate={{ x: [0, 6, 0] }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 0.6,
                    delay,
                    ease: "easeInOut",
                  }}
                >
                  <path
                    d="M9 18L15 12L9 6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              ))}
            </div>
          </button>
        </div>

        <div className="relative group">
          <CustomArrow direction="left" onClick={scrollPrev} disabled={!canScrollPrev} className="hidden md:block" />
          <CustomArrow direction="right" onClick={scrollNext} disabled={!canScrollNext} className="hidden md:block" />

          <div className="overflow-hidden" ref={emblaRef}>
            <div
              className="flex"
              role="region"
              aria-label="Books carousel"
              style={{ willChange: "transform" }}
            >
              {(loading ? Array.from({ length: placeholderCount }) : books).map((item, index) => (
                <div
                  key={item?.id || index}
                  className="
                    flex-[0_0_50%] sm:flex-[0_0_33.333%] md:flex-[0_0_25%]
                    lg:flex-[0_0_16.666%] xl:flex-[0_0_12.5%] px-1
                  "
                  role="group"
                  aria-roledescription="slide"
                >
                  <div className="h-full">
                    {loading ? <SkeletonCard /> : <BookCard book={item} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!loading && (
          <div className="flex justify-center gap-2 mt-2">
            {emblaApi?.scrollSnapList().map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === selectedIndex
                    ? "bg-primary scale-125"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default React.memo(HomeBookSection);
